/**
 * Which languages the interface is covered in.
 *
 * One list, read by everything that needs to agree on it: the store that
 * resolves a visitor's locale, the menu control that offers the choice, the
 * i18next seam that loads the catalogues, and the gate that holds every
 * catalogue complete against English. A locale is covered when it appears here
 * *and* carries a catalogue under `src/locales/`; the gate is what ties the two
 * together, because a code listed with no catalogue is the one mistake this
 * file cannot catch on its own.
 *
 * It sits in `support/` — the bottom of the layer stack — because the store
 * above it and the React seam above that both read it, and it reads nothing.
 *
 * The set is ten languages the product commits to carrying (#614), and no
 * longer every language Google Ads can target (#254): fifty machine-translated
 * catalogues cost a translation bill on every copy change for languages nobody
 * asked for. Each of the ten is still a row of the `Languages` table copied into
 * `src/locales/google-ads-languages.md` — so a language offered here is one the
 * campaign (#223) can target — but the reverse no longer holds, and
 * `googleAdsLanguages.test.ts` checks that direction rather than equality. The
 * `code` is the BCP 47 tag: Google's `zh_CN` becomes `zh-CN`, and every other
 * code is used exactly as Google targets it.
 */

/** Which way a locale is written. Drives `<html dir>` and the layout with it. */
export type TextDirection = "ltr" | "rtl";

export interface LocaleDescriptor {
  /** The BCP 47 code, and the value of `<html lang>`. */
  code: string;
  /** The language's name in that language — what the picker shows. */
  nativeName: string;
  /** The language's name in English, for prose that is not the picker. */
  englishName: string;
  direction: TextDirection;
}

/**
 * The locale every other one falls back to, and the catalogue the gate measures
 * the rest against.
 */
export const FALLBACK_LOCALE = "en";

export const LOCALES: readonly LocaleDescriptor[] = [
  {
    code: "ar",
    nativeName: "العربية",
    englishName: "Arabic",
    direction: "rtl",
  },
  { code: "bn", nativeName: "বাংলা", englishName: "Bengali", direction: "ltr" },
  {
    code: "zh-CN",
    nativeName: "简体中文",
    englishName: "Chinese (simplified)",
    direction: "ltr",
  },
  {
    code: "en",
    nativeName: "English",
    englishName: "English",
    direction: "ltr",
  },
  {
    code: "fr",
    nativeName: "français",
    englishName: "French",
    direction: "ltr",
  },
  { code: "hi", nativeName: "हिन्दी", englishName: "Hindi", direction: "ltr" },
  {
    code: "id",
    nativeName: "Indonesia",
    englishName: "Indonesian",
    direction: "ltr",
  },
  {
    code: "ru",
    nativeName: "Русский",
    englishName: "Russian",
    direction: "ltr",
  },
  {
    code: "es",
    nativeName: "español",
    englishName: "Spanish",
    direction: "ltr",
  },
  { code: "ur", nativeName: "اردو", englishName: "Urdu", direction: "rtl" },
];

export const LOCALE_CODES: readonly string[] = LOCALES.map(
  (locale) => locale.code,
);

const BY_CODE = new Map(LOCALES.map((locale) => [locale.code, locale]));

/** Whether the interface carries a catalogue for this exact code. */
export function isCoveredLocale(code: string): boolean {
  return BY_CODE.has(code);
}

export function localeDescriptor(code: string): LocaleDescriptor | undefined {
  return BY_CODE.get(code);
}

/** The writing direction for a code, left-to-right for one nobody covers. */
export function directionOf(code: string): TextDirection {
  return BY_CODE.get(code)?.direction ?? "ltr";
}

/**
 * The covered locale a requested tag asks for, or `null`.
 *
 * A browser asks for `ru-RU`, `en-GB`, `pt-BR`; the catalogues are keyed by
 * language alone. Matching the exact tag first leaves room for a regional
 * catalogue later without this rule changing: `pt-BR` would win over `pt` for a
 * browser that asked for it, and fall to `pt` for one that did not.
 */
export function matchLocale(requested: string): string | null {
  const tag = requested.trim().toLowerCase();
  if (!tag) return null;
  const exact = LOCALE_CODES.find((code) => code.toLowerCase() === tag);
  if (exact) return exact;
  const base = tag.split("-")[0];
  return LOCALE_CODES.find((code) => code.toLowerCase() === base) ?? null;
}

/**
 * The first covered locale in an ordered list of requests, or the fallback.
 *
 * The list is the visitor's own order of preference — what a browser exposes as
 * `navigator.languages` and sends as `Accept-Language` — so the first match
 * wins rather than the best match.
 */
export function resolveLocale(requested: readonly string[]): string {
  for (const tag of requested) {
    const matched = matchLocale(tag);
    if (matched) return matched;
  }
  return FALLBACK_LOCALE;
}

/**
 * The tags an `Accept-Language` header asks for, most-preferred first.
 *
 * The header is the request-side twin of `navigator.languages`, with one
 * difference that matters: it carries weights rather than an order, so
 * `en;q=0.5, ru` asks for Russian first while reading left to right says
 * English. Sorting by weight is what makes the list `resolveLocale` consumes
 * mean the same thing on either side of the seam.
 *
 * `*` is dropped rather than matched: it means "anything", and the fallback
 * already covers that without pretending the visitor asked for English.
 */
export function acceptedLocales(
  header: string | null | undefined,
): readonly string[] {
  if (!header) return [];
  return header
    .split(",")
    .map((part, index) => {
      const semicolon = part.indexOf(";");
      const head = semicolon === -1 ? part : part.slice(0, semicolon);
      const parameters = semicolon === -1 ? "" : part.slice(semicolon + 1);
      const quality = /(?:^|;)\s*q=([0-9.]+)\s*(?:;|$)/i.exec(parameters);
      return {
        tag: head.trim(),
        // A tag with no `q` is `q=1`; `index` keeps the header's own order
        // among equal weights, which `Array.sort` is not obliged to.
        weight: quality ? Number(quality[1]) : 1,
        index,
      };
    })
    .filter(
      (entry) => entry.tag !== "" && entry.tag !== "*" && entry.weight > 0,
    )
    .sort((a, b) => b.weight - a.weight || a.index - b.index)
    .map((entry) => entry.tag);
}
